import classNames from 'classnames/bind';
import React, { useState } from 'react';
import { isSafari, browserVersion, osVersion, isIOS } from 'react-device-detect';
import { Table as SemanticUiTable } from 'semantic-ui-react';
import get from 'lodash/get';
import { TableProps } from './Table.types';
import styles from './Table.module.scss';

const cx = classNames.bind(styles);

const Table = <T extends object>({
  data,
  getKey,
  tableViewConfig,
  className,
  onRowMouseUp,
  autoLayout,
  activeKeys,
  getIsDisabled,
  disabledItemElement: DisabledItemElement,
  clickable,
}: TableProps<T>): React.ReactElement => {
  const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);
  const hasHeader = tableViewConfig.columns.some((column) => column.headerName);
  const hasOlderSafariBrowser = isSafari && Number(browserVersion) < 16;
  const hasOlderIOSVersion = isIOS && Number(osVersion) < 16;

  return (
    <SemanticUiTable
      className={cx('body', className)}
      basic="very"
      fixed={!autoLayout}
      singleLine
      unstackable
      data-testid="table"
    >
      {hasHeader && (
        <SemanticUiTable.Header className={cx('header')}>
          <SemanticUiTable.Row>
            {tableViewConfig.columns.map((column, index) => {
              const {
                dataKey,
                headerName,
                width,
                maxWidth,
                uniqueDataKey,
                headerCellRenderer: HeaderCellRenderer,
                cellRendererProps,
              } = column;
              const headerProps = { props: cellRendererProps as any, item: null, value: null, row: { index } };

              return (
                <SemanticUiTable.HeaderCell
                  className={cx('header-cell')}
                  key={`header-${uniqueDataKey || dataKey}`}
                  style={{ maxWidth, width }}
                >
                  {HeaderCellRenderer ? <HeaderCellRenderer {...headerProps} /> : headerName}
                </SemanticUiTable.HeaderCell>
              );
            })}
          </SemanticUiTable.Row>
        </SemanticUiTable.Header>
      )}
      <SemanticUiTable.Body>
        {data.map((item, index) => {
          const key = getKey(item, index);
          const row = {
            index,
            isDisabled: getIsDisabled?.(item),
            isHovered: hoveredRowIndex === index,
          };
          return (
            <SemanticUiTable.Row
              key={key}
              className={cx('row', {
                'is-active': activeKeys?.includes(key),
                'is-disabled': row.isDisabled,
                clickable: clickable || !!onRowMouseUp,
              })}
              onMouseEnter={() => setHoveredRowIndex(index)}
              onMouseLeave={() => setHoveredRowIndex(null)}
              data-testid="table-row"
            >
              {tableViewConfig.columns.map((column, columnIndex) => {
                const {
                  cellDataRenderer: CellDataRenderer,
                  dataKey,
                  width,
                  maxWidth,
                  cellRendererProps,
                  cellClassname,
                  uniqueDataKey,
                  stopMouseUpPropagation,
                  dataViewItemConfiguration,
                  cellLinkRenderer: CellLinkRenderer,
                } = column;
                const cellValue = get(item, dataKey);
                const formatterProps = {
                  value: cellValue,
                  item,
                  props: { ...(cellRendererProps as any), onItemClick: onRowMouseUp },
                  row,
                  configuration: dataViewItemConfiguration,
                };
                const shouldRenderCell =
                  !row.isDisabled ||
                  !row.isHovered ||
                  columnIndex < tableViewConfig.columns.length - 1 ||
                  !DisabledItemElement;

                return (
                  <SemanticUiTable.Cell
                    key={`${key}-cell-${uniqueDataKey || dataKey}`}
                    // Relative cell fixes CSNP-2251, as relative table rows are not supported by older Safari/WebKit browsers
                    className={cx('cell', cellClassname, { relative: hasOlderSafariBrowser || hasOlderIOSVersion })}
                    style={{ maxWidth, width }}
                    data-testid="table-cell"
                    onMouseUp={(e) => {
                      if (stopMouseUpPropagation) {
                        e.stopPropagation();
                      }
                    }}
                  >
                    {shouldRenderCell ? (
                      <>
                        {CellLinkRenderer && <CellLinkRenderer {...formatterProps} />}
                        <span data-testid={uniqueDataKey || dataKey} className={cx('cell-content')}>
                          {CellDataRenderer ? <CellDataRenderer {...formatterProps} /> : cellValue}
                        </span>
                      </>
                    ) : (
                      <span>
                        <DisabledItemElement
                          item={item}
                          configuration={tableViewConfig.disabled?.dataViewItemConfiguration}
                          row={row}
                          props={{ onItemClick: onRowMouseUp, ...tableViewConfig.disabled?.dataRendererProps }}
                        />
                      </span>
                    )}
                  </SemanticUiTable.Cell>
                );
              })}
            </SemanticUiTable.Row>
          );
        })}
      </SemanticUiTable.Body>
    </SemanticUiTable>
  );
};

export default Table;
